import type { PlanId } from 'Billing/Catalog';
import type {
  CdvLocalTransaction,
  CdvPurchaseOffer,
  CdvPurchaseProduct,
  CdvPricingPhase,
} from './initPurchases';

const PERIODS: Record<PlanId, string> = { monthly: 'P1M', '3-months': 'P3M', annual: 'P1Y' };
export interface RecurringOffer {
  offer: CdvPurchaseOffer;
  phase: CdvPricingPhase;
}

/** Do not let getOffer() implicitly select a free trial, prepaid plan, or promotional offer. */
export function selectRecurringOffer(
  product: CdvPurchaseProduct | undefined,
  plan: PlanId,
): RecurringOffer | undefined {
  if (!product?.canPurchase) return;
  const offers =
    product.offers ?? [product.getOffer?.()].filter((offer): offer is CdvPurchaseOffer => !!offer);
  const candidates = offers.filter((offer) => {
    const phases = offer.pricingPhases;
    if (offer.canPurchase === false || phases?.length !== 1) return false;
    const phase = phases[0];
    return (
      phase.recurrenceMode === 'INFINITE_RECURRING' &&
      (phase.billingPeriod === PERIODS[plan] ||
        (plan === 'annual' && phase.billingPeriod === 'P12M')) &&
      Number.isSafeInteger(phase.priceMicros) &&
      phase.priceMicros > 0 &&
      !!phase.price &&
      /^[a-z]{3}$/i.test(phase.currency)
    );
  });
  // An ambiguous catalog must be fixed in the store, never resolved by array ordering.
  if (candidates.length !== 1) return;
  return { offer: candidates[0], phase: candidates[0].pricingPhases![0] };
}

/**
 * What a plan the App Store account already holds says where its price would be (MUS-634). The
 * device cannot tell a subscription still running from one that has lapsed (see below), so the
 * words are true of both.
 */
export const APPLE_ID_HOLDS_PLAN_MESSAGE = 'This Apple ID has bought this plan before.';
/** Followed by a Restore purchases link and a full stop. */
export const APPLE_ID_HOLDS_PLAN_RESTORE_LEAD = 'If it is still active,';

const HOLDING_STATES = ['approved', 'finished'];

/**
 * True when the store will not sell this plan because the App Store account on this device already
 * holds it (MUS-634), not because the plan is missing from the store. The purchase plugin marks a
 * product not purchasable while the transaction it finds for the product on this device is
 * not consumed, not pending and not past an expiry date. On the App Store (StoreKit 1) a
 * transaction never carries an expiry date, so a bought or renewing subscription the store has
 * handed over (approved, or finished) keeps its plan that way, and so does a lapsed one the store
 * hands over again (a restore, or a transaction the app has not finished). A purchase still under
 * way (initiated or pending) is not a plan held.
 */
export function appleIdHoldsProduct(
  product: CdvPurchaseProduct | undefined,
  transaction: CdvLocalTransaction | undefined,
): boolean {
  return (
    !!product && product.canPurchase === false && HOLDING_STATES.includes(transaction?.state ?? '')
  );
}
